import {
  AgxButton,
  AgxDropdown,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_Refresh,
  useToast,
  type DropdownOption,
} from '@urbanx/agx.ui-components2';
import { useGetAllAgencies } from '@urbanx/agx.agencies.client';
import {
  SoftDeletedAgentSummary,
  useGetSoftDeletedAgentsForAgency,
  useInvalidateUseGetSoftDeletedAgentsForAgency,
  useRestoreSoftDeletedAgent,
} from '@urbanx/agx.support.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { useMemo, useState } from 'react';

import SupportBackButton from '../SupportBackButton';

const getAgentName = (agent?: SoftDeletedAgentSummary) =>
  agent?.name?.fullName ||
  [agent?.name?.firstName, agent?.name?.lastName].filter((namePart) => !!namePart).join(' ') ||
  'Unnamed agent';

const getAgentDropdownLabel = (agent: SoftDeletedAgentSummary) => {
  const agentName = getAgentName(agent);
  const agentEmail = agent.agentId || agent.contactEmail;
  return agentEmail ? `${agentName} (${agentEmail})` : agentName;
};

const RestoreSoftDeletedAgent = () => {
  const { pushToast } = useToast();
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data: agencies, isPending: isLoadingAgencies } = useGetAllAgencies();

  const {
    data: softDeletedAgents,
    isPending: isLoadingAgents,
    isError: hasAgentsError,
  } = useGetSoftDeletedAgentsForAgency(selectedAgencyId, !!selectedAgencyId);

  const invalidateSoftDeletedAgents = useInvalidateUseGetSoftDeletedAgentsForAgency();

  const { restoreSoftDeletedAgent, restoreSoftDeletedAgentPending } = useRestoreSoftDeletedAgent({
    onSuccess: async (_returnData, inputData) => {
      setShowConfirmModal(false);
      setSelectedAgentId('');

      if (inputData.agencyId) {
        await invalidateSoftDeletedAgents(inputData.agencyId);
      }

      pushToast({
        message: 'Agent restored',
        type: 'success',
      });
    },
    onError: () => {
      pushToast({
        message: 'Unable to restore agent',
        type: 'error',
      });
    },
  });

  const agencyOptions = useMemo<DropdownOption[]>(
    () =>
      (agencies ?? [])
        .filter((agency) => !!agency.id)
        .map((agency) => ({
          label: agency.name ?? 'Unnamed agency',
          value: agency.id!,
          searchText: agency.name ?? '',
        })),
    [agencies]
  );

  const agentOptions = useMemo<DropdownOption[]>(
    () =>
      (softDeletedAgents ?? [])
        .filter((agent) => !!agent.agentId)
        .map((agent) => ({
          label: getAgentDropdownLabel(agent),
          value: agent.agentId!,
          searchText: `${getAgentName(agent)} ${agent.agentId ?? ''} ${agent.contactEmail ?? ''}`,
        })),
    [softDeletedAgents]
  );

  const selectedAgency = agencies?.find((agency) => agency.id === selectedAgencyId);
  const selectedAgent = softDeletedAgents?.find((agent) => agent.agentId === selectedAgentId);
  const canRestore = !!selectedAgencyId && !!selectedAgentId && !restoreSoftDeletedAgentPending;

  const onAgencyChange = (agencyId: string) => {
    setSelectedAgencyId(agencyId);
    setSelectedAgentId('');
  };

  const onRestore = async () => {
    if (!canRestore) return;

    await restoreSoftDeletedAgent({
      agencyId: selectedAgencyId,
      agentId: selectedAgentId,
    });
  };

  return (
    <AgxSpacer direction="vertical" gap="spacing-xl" fill>
      <SupportBackButton />

      <Panel title="Restore Soft Deleted Agent">
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxSpacer
            direction="horizontal"
            gap="spacing-lg"
            fill
            contentAlignment="end"
            style={{ flexWrap: 'wrap' }}
          >
            <AgxDropdown
              id="restore-soft-deleted-agent-agency"
              label="Agency"
              selected={selectedAgencyId}
              options={agencyOptions}
              onChange={onAgencyChange}
              placeholder="Select agency"
              searchable
              searchPlaceholder="Search agencies"
              stretch
              hideOptionalText
              disabled={isLoadingAgencies || restoreSoftDeletedAgentPending}
            />

            {selectedAgencyId && !isLoadingAgents && (
              <>
                <AgxDropdown
                  id="restore-soft-deleted-agent-agent"
                  label="Soft deleted agent"
                  selected={selectedAgentId}
                  options={agentOptions}
                  onChange={setSelectedAgentId}
                  placeholder="Select agent"
                  searchable
                  searchPlaceholder="Search agents"
                  stretch
                  hideOptionalText
                  disabled={restoreSoftDeletedAgentPending || agentOptions.length === 0}
                />

                <AgxButton
                  id="restore-soft-deleted-agent-submit"
                  text={restoreSoftDeletedAgentPending ? 'Restoring' : 'Restore'}
                  variant="primary"
                  leftIcon={<Icon_Refresh width={16} height={16} />}
                  disabled={!canRestore}
                  onClick={() => setShowConfirmModal(true)}
                />
              </>
            )}
          </AgxSpacer>

          {(isLoadingAgencies || (!!selectedAgencyId && isLoadingAgents)) && <LoadingState />}

          {hasAgentsError && (
            <AgxTypography id="restore-soft-deleted-agent-error" type="body" size="small">
              Unable to load soft deleted agents.
            </AgxTypography>
          )}

          {selectedAgencyId && !isLoadingAgents && !hasAgentsError && agentOptions.length === 0 && (
            <AgxTypography id="restore-soft-deleted-agent-empty" type="body" size="small">
              No soft deleted agents were found for this agency.
            </AgxTypography>
          )}
        </AgxSpacer>
      </Panel>

      <AgxModal
        id="restore-soft-deleted-agent-confirm-modal"
        title="Confirm Agent Restore"
        titleIcon={<Icon_AlertCircleOutline type="Danger" height={40} width={40} />}
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withDivider"
        isOpen={showConfirmModal}
        setIsDialogOpen={setShowConfirmModal}
        actionButtons={
          <AgxSpacer direction="horizontal" gap="spacing-md" contentJustification="center" fill>
            <AgxButton
              id="restore-soft-deleted-agent-cancel"
              text="Cancel"
              variant="hollow"
              stretch
              disabled={restoreSoftDeletedAgentPending}
              onClick={() => setShowConfirmModal(false)}
            />
            <AgxButton
              id="restore-soft-deleted-agent-confirm"
              text={restoreSoftDeletedAgentPending ? 'Restoring' : 'Restore'}
              variant="primary"
              stretch
              disabled={restoreSoftDeletedAgentPending}
              onClick={() => void onRestore()}
            />
          </AgxSpacer>
        }
        content={
          <AgxSpacer direction="vertical" gap="spacing-md" padding="spacing-4xl">
            <AgxTypography id="restore-soft-deleted-agent-confirm-copy" type="body" size="medium">
              Restore {getAgentName(selectedAgent)} for{' '}
              {selectedAgency?.name ?? 'the selected agency'} by setting softDeleted to false.
            </AgxTypography>
          </AgxSpacer>
        }
      />
    </AgxSpacer>
  );
};

export default RestoreSoftDeletedAgent;
